import { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../i18n';

const STORAGE_KEY = 'gdpr_consent_v1';

const TEXTS = {
    title: { da: 'Cookies & privatliv', en: 'Cookies & privacy', de: 'Cookies & Datenschutz', sv: 'Cookies & integritet', no: 'Cookies & personvern', es: 'Cookies y privacidad', fr: 'Cookies et confidentialité', it: 'Cookie e privacy', pt: 'Cookies e privacidade', nl: 'Cookies & privacy', pl: 'Pliki cookie i prywatność' },
    body: {
        da: 'Analyse og annoncer indlæses kun efter dit samtykke. Du kan altid vælge kun nødvendige cookies.',
        en: 'Analytics and advertisements load only after your consent. You can always choose necessary cookies only.',
        de: 'Google Analytics wird erst nach Ihrer Einwilligung geladen.',
        sv: 'Google Analytics laddas först efter ditt samtycke.',
        no: 'Google Analytics lastes først etter ditt samtykke.',
        es: 'Google Analytics solo se carga con tu consentimiento.',
        fr: 'Google Analytics se charge uniquement après votre consentement.',
        it: 'Google Analytics si carica solo dopo il consenso.',
        pt: 'O Google Analytics só carrega após o consentimento.',
        nl: 'Google Analytics wordt alleen na je toestemming geladen.',
        pl: 'Google Analytics ładuje się dopiero po wyrażeniu zgody.',
    },
    accept: { da: 'Acceptér', en: 'Accept', de: 'Akzeptieren', sv: 'Acceptera', no: 'Aksepter', es: 'Aceptar', fr: 'Accepter', it: 'Accetta', pt: 'Aceitar', nl: 'Accepteren', pl: 'Akceptuj' },
    analytics_only: { da: 'Kun analyse', en: 'Analytics only', de: 'Nur Analyse', sv: 'Endast analys', no: 'Kun analyse', es: 'Solo análisis', fr: 'Analyses uniquement', it: 'Solo analisi', pt: 'Só análise', nl: 'Alleen analyse', pl: 'Tylko analityka' },
    necessary_only: { da: 'Kun nødvendige', en: 'Necessary only', de: 'Nur notwendige', sv: 'Endast nödvändiga', no: 'Bare nødvendige', es: 'Solo necesarias', fr: 'Nécessaires uniquement', it: 'Solo necessari', pt: 'Apenas necessárias', nl: 'Alleen noodzakelijke', pl: 'Tylko niezbędne' },
    learn_more: { da: 'Læs mere', en: 'Learn more', de: 'Mehr erfahren', sv: 'Läs mer', no: 'Les mer', es: 'Más información', fr: 'En savoir plus', it: 'Scopri di più', pt: 'Saber mais', nl: 'Meer info', pl: 'Dowiedz się więcej' },
};

export default function CookieBanner() {
    const { locale } = useI18n();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            const v = JSON.parse(localStorage.getItem(STORAGE_KEY));
            if (!v || v.version !== 2) setOpen(true);
        } catch { setOpen(true); }
        const reopen = () => setOpen(true);
        window.addEventListener('growdaily:cookie-settings', reopen);
        return () => window.removeEventListener('growdaily:cookie-settings', reopen);
    }, []);

    const save = async (analytics, marketing) => {
        let previouslyAllowed = false;
        try { previouslyAllowed = JSON.parse(localStorage.getItem(STORAGE_KEY))?.analytics === true; } catch {}
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 2, necessary: true, analytics, marketing, ts: Date.now() })); } catch {}
        try { window.growdailyAnalyticsConsent?.(analytics); } catch {}
        // The user's choice must dismiss immediately.  In particular, a local
        // debug build may not have a reachable API while Eden is still drawing
        // its first frame; waiting for that request keeps the event handler
        // alive long enough for lower-end WebViews to report an ANR.
        // Local storage is the source of truth for this browser, while the
        // server receives a best-effort audit copy in the background.
        void api.post('/consent', { necessary: true, analytics, marketing }).catch(() => {});
        setOpen(false);
        // Unload an already-running Google script when withdrawing consent.
        if (previouslyAllowed && !analytics) window.location.reload();
    };

    if (!open) return null;

    const get = (k) => TEXTS[k][locale] || TEXTS[k][(locale || '').split('-')[0]] || TEXTS[k].en;

    return (
        <div data-testid="cookie-banner" className="fixed inset-x-3 bottom-banner-safe sm:left-auto sm:right-4 sm:max-w-md z-[100] animate-fade-up">
            <div className="bg-brand-card border border-brand-border rounded-2xl shadow-lg p-4">
                <div className="flex items-start gap-3">
                    <div className="bg-brand-ochre/20 text-brand-ochre w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Cookie size={18} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="font-serif text-base text-brand-ink">{get('title')}</h4>
                        <p className="text-xs text-brand-ink2 mt-1 leading-relaxed">{get('body')}</p>
                        <Link to="/privacy" className="inline-block text-xs text-brand-green font-medium mt-1 hover:underline">
                            {get('learn_more')} →
                        </Link>
                    </div>
                    <button data-testid="btn-close-banner" onClick={() => save(false, false)} className="text-brand-ink3 hover:text-brand-ink p-1 -mt-1">
                        <X size={16} />
                    </button>
                </div>
                <div className="mt-3 flex gap-2">
                    <button data-testid="btn-cookie-necessary" onClick={() => save(false, false)} className="flex-1 bg-brand-bg2 hover:bg-brand-border text-brand-ink py-2 rounded-full text-xs font-medium transition-colors">
                        {get('necessary_only')}
                    </button>
                    <button data-testid="btn-cookie-analytics" onClick={() => save(true, false)} className="flex-1 bg-brand-bg2 hover:bg-brand-border text-brand-ink py-2 rounded-full text-xs font-medium transition-colors">
                        {get('analytics_only')}
                    </button>
                    <button data-testid="btn-cookie-accept" onClick={() => save(true, true)} className="flex-1 bg-brand-green hover:bg-brand-greenDark text-white py-2 rounded-full text-xs font-medium transition-colors">
                        {get('accept')}
                    </button>
                </div>
            </div>
        </div>
    );
}
